import React, { useState, useEffect } from 'react';
import { Edit, Save, X, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

interface Achievement {
  id: number;
  title: string;
  icon: string;
  value: number;
  suffix: string;
  order: number;
  is_active: boolean;
}

const AchievementList: React.FC = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<number>(0);
  const [completedProjectsCount, setCompletedProjectsCount] = useState(0);

  const api = axios.create({
    baseURL: `${API_BASE_URL}/api/admin`,
    withCredentials: true,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    }
  });

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      console.log('Fetching achievements from API...');
      const response = await api.get('/achievements');
      
      console.log('API Response:', response.data);
      
      if (response.data.success) {
        setAchievements(response.data.data);
        setCompletedProjectsCount(response.data.completed_projects_count || 0);
        console.log('Achievements loaded:', response.data.data.length);
      } else {
        toast.error('Failed to fetch achievements');
      }
    } catch (error: any) {
      console.error('Fetch error:', error);
      
      // Check if it's an auth error
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Please login again');
        // Redirect to login if needed
        // window.location.href = '/login';
      } else {
        toast.error(error.response?.data?.message || 'Failed to fetch achievements');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAchievements();
  }, []);

  const handleUpdate = async (id: number) => {
    try {
      const response = await api.put(`/achievements/${id}`, {
        value: editValue,
      });
      
      if (response.data.success) {
        toast.success('Achievement updated successfully');
        setEditingId(null);
        fetchAchievements();
      }
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update achievement');
    }
  };

  const handleUpdateCompletedProjects = async () => {
    try {
      const response = await api.post('/achievements/update-completed-projects');
      
      if (response.data.success) {
        toast.success(`Completed projects updated to ${response.data.completed_projects_count}`);
        fetchAchievements();
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update completed projects');
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      await api.put(`/achievements/${id}`, {
        is_active: !currentStatus,
      });
      
      toast.success(`Achievement ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchAchievements();
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="ml-3 text-gray-600">Loading achievements...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Achievements</h1>
          <p className="text-gray-600">Manage achievements displayed on the website</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchAchievements}
            className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
          >
            <RefreshCw size={18} />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleUpdateCompletedProjects}
            className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <CheckCircle size={18} />
            <span>Update Completed Projects ({completedProjectsCount})</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-blue-700">
          <strong>Completed Projects:</strong> {completedProjectsCount} active projects
          <span className="text-sm text-blue-500 ml-2">
            (Auto-updated from Projects)
          </span>
        </p>
        <p className="text-sm text-gray-600 mt-1">
          Total Achievements: {achievements.length}
        </p>
      </div>

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {achievements.map((achievement) => (
          <div
            key={achievement.id}
            className={`bg-white rounded-xl border p-6 shadow-sm ${
              !achievement.is_active ? 'opacity-60' : ''
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-2xl">
                  <i className={achievement.icon}></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">{achievement.title}</h3>
                  <p className="text-sm text-gray-500">
                    Order: {achievement.order} | Status: {achievement.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleToggleStatus(achievement.id, achievement.is_active)}
                  className={`p-2 rounded-lg transition-colors ${
                    achievement.is_active
                      ? 'text-green-600 hover:bg-green-50'
                      : 'text-red-600 hover:bg-red-50'
                  }`}
                  title={achievement.is_active ? 'Deactivate' : 'Activate'}
                >
                  {achievement.is_active ? <CheckCircle size={18} /> : <XCircle size={18} />}
                </button>
              </div>
            </div>

            <div className="mt-4">
              {editingId === achievement.id ? (
                <div className="flex items-center space-x-3">
                  <input
                    type="number"
                    min="0"
                    value={editValue}
                    onChange={(e) => setEditValue(parseInt(e.target.value) || 0)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => handleUpdate(achievement.id)}
                    className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Save size={18} />
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {achievement.title !== 'Completed projects' && (
                      <button
                        onClick={() => {
                          setEditingId(achievement.id);
                          setEditValue(achievement.value);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit value"
                      >
                        <Edit size={18} />
                      </button>
                    )}
                    <p className="text-3xl font-bold text-gray-800">
                      {achievement.value}
                      <span className="text-xl text-gray-500">{achievement.suffix}</span>
                    </p>
                  </div>
                  {achievement.title === 'Completed projects' && (
                    <span className="text-sm text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                      Auto-updated
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-gray-50 rounded-lg p-3 text-center text-sm text-gray-500">
        <p>✅ Connected to Laravel API | {achievements.length} achievements loaded</p>
        <p className="text-xs mt-1">
          💡 "Completed projects" is auto-updated from Projects count
        </p>
      </div>
    </div>
  );
};

export default AchievementList;